/**
 * Client-side service for managing app demos
 * This provides a convenient interface for frontend components to interact
 * with the app demo API endpoints
 */

// Import our html2canvas utility instead of direct import
import html2canvas from '../utils/html2canvasImport';

/**
 * Create a new app demo with the provided configuration
 * @param {Object} config - App demo configuration
 * @returns {Promise<Object>} - Created app demo
 */
export const createAppDemo = async (config) => {
    try {
        const response = await fetch('/api/app-demos', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({ config })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to create app demo');
        }

        return await response.json();
    } catch (error) {
        console.error('Error creating app demo:', error);
        throw error;
    }
};

/**
 * Get an app demo by ID
 * @param {string} id - App demo ID
 * @returns {Promise<Object>} - App demo
 */
export const getAppDemo = async (id) => {
    try {
        const response = await fetch(`/api/app-demos/${id}`, {
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to get app demo');
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting app demo:', error);
        throw error;
    }
};

/**
 * Save screenshots for an app demo
 * @param {string} appDemoId - App demo ID
 * @param {Array<string>} screenshotUrls - Screenshot URLs
 * @returns {Promise<Object>} - Response
 */
export const saveAppDemoScreenshots = async (appDemoId, screenshotUrls) => {
    try {
        const response = await fetch(`/api/app-demos/${appDemoId}/screenshots`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({ screenshot_urls: screenshotUrls })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to save screenshots');
        }

        return await response.json();
    } catch (error) {
        console.error('Error saving screenshots:', error);
        throw error;
    }
};

/**
 * Get app demos for the current user
 * @returns {Promise<Array>} - User's app demos
 */
export const getUserAppDemos = async () => {
    try {
        const response = await fetch('/api/app-demos', {
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to get user app demos');
        }

        const data = await response.json();
        return data.app_demos || [];
    } catch (error) {
        console.error('Error getting user app demos:', error);
        throw error;
    }
};

/**
 * Capture a screenshot of the app preview
 * This is a client-side helper that uses html2canvas
 * @param {HTMLElement} element - DOM element to capture
 * @returns {Promise<string>} - Data URL of the screenshot
 */
export const captureAppScreenshot = async (element) => {
    try {
        // Use our imported html2canvas utility
        const canvas = await html2canvas(element, {
            scale: 2, // Higher quality
            useCORS: true, // Allow cross-origin images
            allowTaint: true,
            backgroundColor: null // Transparent background
        });
        
        return canvas.toDataURL('image/png');
    } catch (error) {
        console.error('Error capturing screenshot:', error);
        throw error;
    }
};

/**
 * Generate a share link for an app demo
 * @param {string} appDemoId - App demo ID
 * @returns {string} - Share link
 */
export const generateShareLink = (appDemoId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/app-demo-share/${appDemoId}`;
};

/**
 * Send an app demo share link via email
 * @param {string} appDemoId - App demo ID
 * @param {string} email - Recipient email
 * @returns {Promise<Object>} - Response
 */
export const shareAppDemoViaEmail = async (appDemoId, email) => {
    try {
        const response = await fetch(`/api/app-demos/${appDemoId}/share`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({ 
                method: 'email',
                recipient: email
            })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to share app demo');
        }

        return await response.json();
    } catch (error) {
        console.error('Error sharing app demo via email:', error);
        throw error;
    }
};

/**
 * Generate custom app content using OpenAI based on user selections
 * @param {Object} appData - App data including all user selections
 * @returns {Promise<Object>} - Generated content including screens, text, and layouts
 */
export const generateOpenAIAppContent = async (appData) => {
    try {
        const response = await fetch('/api/openai/generate-app-content', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(appData)
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to generate content with OpenAI');
        }

        return await response.json();
    } catch (error) {
        console.error('Error generating content with OpenAI:', error);
        throw error;
    }
}; 